import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useMemo } from "react";
import { formatCount } from "../../../../../utils/D3/helper";

const QUARTERS = { 1: "Q1", 2: "Q2", 3: "Q3", 4: "Q4" };

const TABLE_SX = {
  borderColor: "#eae9ee",
  borderRadius: "6px",
  maxHeight: 377,
  width: "100%",
  pt: { md: 1, sm: 0, xs: 0 },
  pl: { md: 1, sm: 0, xs: 0 },
  "& th": { fontWeight: 600, background: "#fbfbfc", zIndex: 0 },
  "& th, td": {
    fontSize: ".8rem",
    color: "#434166",
    padding: "2px 6px",
    border: 1,
    borderColor: "#eae9ee",
  },
  "&::-webkit-scrollbar-thumb": { background: "#020e4a29", borderRadius: "1px" },
  "&:hover::-webkit-scrollbar-thumb": { background: "#020e4a85", borderRadius: "1px" },
  "&::-webkit-scrollbar-track": { background: "#f2f2f2", borderRadius: "1px" },
  "&::-webkit-scrollbar": { width: "5px !important", height: "5px !important" },
};

const getQuarter = (utcMonth) => QUARTERS[Math.ceil(utcMonth / 3)];

const formatCell = (value, hasData) => {
  if (!hasData) return "-";
  return formatCount(Math.abs(value), true, 1, Math.sign(value) || 1);
};

const HistoricalDataTable = ({ historicaldata }) => {
  const years = useMemo(() => {
    if (!Array.isArray(historicaldata) || historicaldata.length === 0) return [];

    const dataByYear = historicaldata.reduce((acc, item) => {
      if (!item || typeof item.recordDate !== 'string') return acc;

      const dateObj = new Date(item.recordDate);
      if (isNaN(dateObj.getTime())) return acc;

      const year = dateObj.getUTCFullYear();
      const quarter = getQuarter(dateObj.getUTCMonth() + 1);

      if (!acc[year]) {
        acc[year] = { year, Q1: 0, Q2: 0, Q3: 0, Q4: 0, Q1set: false, Q2set: false, Q3set: false, Q4set: false };
      }

      if (item.value != null) {
        acc[year][quarter] = item.value;
        acc[year][`${quarter}set`] = true;
      }

      return acc;
    }, {});

    return Object.values(dataByYear).sort((a, b) => b.year - a.year);
  }, [historicaldata]);

  if (years.length === 0) return null;

  return (
    <TableContainer sx={TABLE_SX}>
      <Table stickyHeader size="small" aria-label="Historical quarterly data">
        <TableHead>
          <TableRow>
            <TableCell align="left">Year</TableCell>
            <TableCell align="center">Q1</TableCell>
            <TableCell align="center">Q2</TableCell>
            <TableCell align="center">Q3</TableCell>
            <TableCell align="center">Q4</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {years.map((yearData) => (
            <TableRow key={yearData.year} hover>
              <TableCell align="left" sx={{ background: "#e3e8ee" }}>
                {yearData.year}
              </TableCell>
              <TableCell align="right">{formatCell(yearData.Q1, yearData.Q1set)}</TableCell>
              <TableCell align="right">{formatCell(yearData.Q2, yearData.Q2set)}</TableCell>
              <TableCell align="right">{formatCell(yearData.Q3, yearData.Q3set)}</TableCell>
              <TableCell align="right">{formatCell(yearData.Q4, yearData.Q4set)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default React.memo(HistoricalDataTable);